import type { ReactNode } from 'react';
import { memo } from 'react';

import type { Spacing } from '@shieldpay/theme-provider-ui/theme';

import { Box } from '../box/box';
import { InputHelp } from '../input-help/input-help';
import type { LabelVariant } from '../label/label';
import { Label } from '../label/label';
import { ScreenReaderText } from '../screen-reader-text/screen-reader-text';

import { ValidationMessages } from './utilities';

export interface FieldContainerBaseProps {
  screenReaderOnlyLabel?: boolean;
  helpText?: ReactNode;
  label?: ReactNode;
  disabled?: boolean;
  name: string;
  variant?: keyof typeof variantConfig;
  /**
   * Validation is only displayed when showValidation is true
   * in combination with validation messages. Use this prop to
   * override and force errors to be hidden or shown based on
   * external state,e.g. to hide the invalid state again once
   * a field has been interacted with
   */
  labelVariant?: LabelVariant;
  errors?: string[] | undefined;
  'aria-invalid'?: boolean | undefined;
  errorMessageId?: string | undefined;
  showField?: boolean;
}

export interface FieldContainerProps extends FieldContainerBaseProps {
  children: ReactNode;
}

const variantConfig = {
  default: { spacing: 'baseNeg4', isFieldGroup: false },
  fieldGroup: { spacing: 'basePos1', isFieldGroup: true },
  cardControls: { spacing: 'basePos4', isFieldGroup: true },
} satisfies Record<string, { spacing: Spacing; isFieldGroup: boolean }>;

export const FieldContainer = memo(function FieldContainer({
  children,
  label,
  labelVariant,
  helpText,
  screenReaderOnlyLabel = false,
  name,
  variant = 'default',
  disabled = false,
  errors,
  'aria-invalid': invalid,
  errorMessageId,
  showField = true,
}: FieldContainerProps) {
  if (showField === false) {
    return null;
  }

  const { isFieldGroup, spacing } = variantConfig[variant];

  const extraProps = isFieldGroup
    ? {
        // Because <fieldset /> and <legend /> have some interesting display quirks
        // use role and aria-labelledby attributes to get the same semantic behaviour.
        role: 'group',
        'aria-labelledby': `label-for-${name}`,
        'aria-invalid': invalid,
        'aria-errormessage': errorMessageId,
      }
    : { 'aria-labelledby': undefined };

  return (
    <Box spacing={spacing} {...extraProps}>
      {label ? (
        <Label
          htmlFor={name}
          id={extraProps['aria-labelledby']}
          disabled={disabled}
          variant={labelVariant}
        >
          {screenReaderOnlyLabel ? (
            <ScreenReaderText>{label}</ScreenReaderText>
          ) : (
            label
          )}
        </Label>
      ) : null}
      {children}
      {helpText ? <InputHelp>{helpText}</InputHelp> : null}
      {invalid && errors?.length && errorMessageId ? (
        <ValidationMessages errors={errors} id={errorMessageId} />
      ) : null}
    </Box>
  );
});

export { type ValidationProps, validationToFieldAria } from './utilities';
